/**
 * /pressure-testing/tickets/:ticketId — one field ticket on its own page (PT-5,
 * fieldiq-api#932 `GET /pressure-testing/field-tickets/:id`). A row on the
 * Test Tickets register, a test job's Tickets card, or a ticket number under
 * Ready to invoice opens it here. It was a slide-over drawer until split panes
 * and drawers beside a list were banned (CLAUDE.md); the back arrow returns to
 * the list with its status chip and search as they were left, or to whatever
 * page opened it.
 *
 * EVERY FIGURE IS THE API'S. Each line's amount and its money verdict
 * (`money`: priced, no_charge, no_quantity, no_rate), the subtotal, the status
 * and what may change are read from `GET /field-tickets/:id` as returned. A
 * line with no rate reads "No rate" with the API's reason, never $0; an
 * explicit 0 reads "No charge". Nothing here multiplies, adds or decides that a
 * ticket is locked — the actions follow the status the API sent, and when the
 * API refuses anyway its sentence is shown.
 *
 * ACTIONS, for PT_DISPATCH (the API's requireRole; a button it would refuse is
 * left off):
 *   draft     add / edit / remove lines (incl. set or override a rate, or $0
 *             "No charge") · Reprice · Void · PDF
 *   signed    Approve · Void · PDF
 *   approved  Void · PDF
 *   invoiced  PDF. "Invoiced on PTI-NNNN" links to the invoice (PT-6); the
 *             invoice is voided to bill the ticket again, never the ticket.
 *   void      PDF (stamped VOID). A replacement is built from the job's
 *             Tickets card (or by closing the day again on a per-day job).
 * Signing is the company man's, on the technician's phone (field/
 * PtFieldTicketScreen); the office does not sign here. PDF, Reprice, Void and
 * Approve sit on the band, top right; Add line and each line's edit / remove
 * stay with the lines they act on.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileText, Loader2, Pencil, Plus, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useApprovePtTicket, usePtTicket, usePtTicketInvoice, useRepricePtTicket, useVoidPtTicket } from '../../hooks/usePressureTesting';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../lib/blob-tab';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchPt,
  ptLineAmountText,
  ptLineKindLabel,
  ptLineNeedsAttention,
  ptQtyText,
  ptRateText,
  ptSubtotalText,
  ptTicketModeLabel,
  ptTicketStatusSkin,
} from '../../lib/pressure-testing';
import type { PtTicketDetail, PtTicketLine } from '../../types/pressure-testing';
import { PT_BAND_SECONDARY, PT_INPUT, PT_SECONDARY, PtDialog, PtField, PtSkinBadge, PtSpec } from './PtBits';
import { FormError, ptWriteError } from './PtJobDialogs';
import { PT_OFFICE_LINE_KINDS, PtAddLineForm, PtEditLineForm, PtRemoveLineForm } from './PtTicketLineForms';
import { useBackToList } from '../downhole/recordPage';

const LIST = '/pressure-testing/tickets';

const day = (v: string | null | undefined) => formatCalendarDate(v) ?? '—';

/** What a ticket covers: its work day, or the whole job. */
export function ptTicketScopeText(t: Pick<PtTicketDetail, 'scope' | 'work_date'>): string {
  if (t.scope === 'day') return t.work_date ? `Day ${day(t.work_date)}` : 'Day';
  if (t.scope === 'final') return 'Final (job completion)';
  if (t.scope === 'job') return 'Whole job';
  return '—';
}

/** Where a line's price came from, as the API recorded it. */
function rateSourceText(l: PtTicketLine): string {
  if (l.rate_source === 'office') return 'Set by the office';
  if (l.rate_source === 'rate_card') return 'Rate card';
  return '';
}

/** Void, with the reason the API requires. */
function VoidDialog({ ticket, onClose }: { ticket: PtTicketDetail; onClose: () => void }) {
  const voidTicket = useVoidPtTicket();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('A reason is required to void a ticket.');
    voidTicket.mutate(
      { ticketId: ticket.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${ticket.ticket_number} voided`);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The ticket was not voided.').message),
      },
    );
  };

  return (
    <PtDialog
      open
      title={`Void ${ticket.ticket_number}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Keep ticket
          </button>
          <button
            type="submit"
            form="pt-void-ticket"
            disabled={voidTicket.isPending}
            data-testid="pt-ticket-void-confirm"
            className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          >
            {voidTicket.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Void ticket
          </button>
        </>
      }
    >
      <form id="pt-void-ticket" onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-fieldiq-text">
          A void ticket is kept for the record and cannot be billed. Its charges are freed, so a new ticket can be built for {ticket.job_number || 'the job'}.
        </p>
        <FormError message={error} />
        <PtField label="Reason">
          <textarea
            className={`${PT_INPUT} h-24 py-2`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            data-testid="pt-ticket-void-reason"
            autoFocus
          />
        </PtField>
      </form>
    </PtDialog>
  );
}

type LineDialog = { kind: 'add' } | { kind: 'edit'; line: PtTicketLine } | { kind: 'remove'; line: PtTicketLine };

export default function FieldTicketDetailScreen() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const query = usePtTicket(ticketId);
  const ticket = query.data?.data ?? null;
  const meta = query.data?.meta;
  const tz = meta?.time_zone ?? null;
  const today = meta?.today ?? null;
  const reprice = useRepricePtTicket();
  const approve = useApprovePtTicket();
  const [voiding, setVoiding] = useState(false);
  const [lineDialog, setLineDialog] = useState<LineDialog | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const failure = queryLoadFailure(query);
  // A 404 is an answer about the id, not about the network.
  const notFound = !ticket && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const invoiceQuery = usePtTicketInvoice(ticket);
  const invoice = invoiceQuery.data ?? null;

  const canAct = canDispatchPt(user?.role);
  const status = ticket?.status ?? null;
  const isDraft = status === 'draft';

  // A refusal describes the ticket as it was; a fresh read replaces it.
  useEffect(() => setActionError(null), [query.dataUpdatedAt]);

  const openPdf = async () => {
    if (!ticket) return;
    setActionError(null);
    // Claimed synchronously, inside the click's activation window (lib/blob-tab.ts).
    const win = claimBlobTab();
    if (!win) {
      setActionError(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setPdfBusy(true);
    try {
      const res = await api.pressureTesting.tickets.pdf(ticket.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      showBlobInTab(win, blob);
    } catch (err) {
      win.close();
      setActionError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const onReprice = () => {
    if (!ticket) return;
    setActionError(null);
    reprice.mutate(
      { ticketId: ticket.id },
      {
        onSuccess: () => toast.success(`${ticket.ticket_number} repriced against the rate card`),
        onError: (err) => setActionError(ptWriteError(err, 'The ticket was not repriced.').message),
      },
    );
  };

  const onApprove = () => {
    if (!ticket) return;
    setActionError(null);
    approve.mutate(
      { ticketId: ticket.id },
      {
        onSuccess: () => toast.success(`${ticket.ticket_number} approved`),
        onError: (err) => setActionError(ptWriteError(err, 'The ticket was not approved.').message),
      },
    );
  };

  const lines = ticket?.lines ?? [];
  const removed = ticket?.removed_lines ?? [];
  const attention = lines.filter(ptLineNeedsAttention);

  const actions = ticket ? (
    <div className="flex flex-wrap gap-2" data-testid="pt-ticket-actions">
      <button type="button" className={PT_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="pt-ticket-pdf">
        {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
        PDF
      </button>
      {canAct && isDraft && (
        <button type="button" className={PT_BAND_SECONDARY} onClick={onReprice} disabled={reprice.isPending} data-testid="pt-ticket-reprice">
          {reprice.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}
          Reprice
        </button>
      )}
      {canAct && status !== 'void' && status !== 'invoiced' && (
        <button type="button" className={PT_BAND_SECONDARY} onClick={() => setVoiding(true)} data-testid="pt-ticket-void">
          <XCircle className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canAct && status === 'signed' && (
        <button
          type="button"
          className="fi-action fi-action--standard fi-action--primary"
          onClick={onApprove}
          disabled={approve.isPending}
          data-testid="pt-ticket-approve"
        >
          {approve.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
          Approve
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="pt-ticket-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Field ticket"
        title={ticket ? ticket.ticket_number || 'Field ticket' : query.isLoading ? 'Loading…' : 'Field ticket'}
        onBack={back}
        backLabel="Back to field tickets"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="pt-ticket-detail">
          {!ticket && query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading the field ticket" />
            </div>
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pt-ticket-not-found">
              This field ticket was not found.
            </p>
          ) : !ticket ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this field ticket" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PtSkinBadge skin={ptTicketStatusSkin(status)} testId="pt-ticket-status" value={status} />
                <Link to={`/pressure-testing/jobs/${ticket.job_id}`} className="text-sm font-bold text-fieldiq-text underline">
                  {ticket.job_number || '—'}
                </Link>
                <span className="text-sm text-fieldiq-muted">{ticket.customer_name || '—'}</span>
              </div>
              {/* A failed BACKGROUND refetch keeps the ticket on screen and says so. */}
              {failure && (
                <p role="status" className="text-xs text-fieldiq-muted">
                  This ticket could not be refreshed just now, so it shows what loaded earlier.
                </p>
              )}
              {status === 'void' && (
                <div role="status" data-testid="pt-ticket-void-note" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {formatInstant(ticket.voided_at, tz) ?? ''}</p>
                  <p className="mt-1 whitespace-pre-wrap">{ticket.void_reason || '—'}</p>
                </div>
              )}
              {ticket.signature && (
                <p data-testid="pt-ticket-signed-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Signed {formatInstant(ticket.signature.signed_at, tz) ?? '—'} by {ticket.signature.signer_name || '—'}
                  {ticket.signature.signer_title ? `, ${ticket.signature.signer_title}` : ''}.
                  {status === 'signed' || status === 'approved' ? ' A signed ticket cannot change; void it and build a new one to correct it.' : ''}
                </p>
              )}
              {ticket.approved_at && (
                <p className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Approved {formatInstant(ticket.approved_at, tz) ?? '—'}.
                </p>
              )}
              {status === 'invoiced' && (
                <p data-testid="pt-ticket-invoiced-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  {invoiceQuery.isLoading ? (
                    'Invoiced.'
                  ) : invoice ? (
                    <>
                      Invoiced on{' '}
                      <Link
                        to={`/pressure-testing/invoices/${invoice.id}`}
                        className="font-mono font-bold text-fieldiq-text underline"
                        data-testid="pt-ticket-invoice-link"
                      >
                        {invoice.invoice_number}
                      </Link>
                      . Void that invoice to bill this ticket again.
                    </>
                  ) : (
                    'Invoiced. The invoice could not be found from here — see Pressure Testing → Invoices.'
                  )}
                </p>
              )}
              {actionError && (
                <p role="alert" data-testid="pt-ticket-action-error" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  {actionError}
                </p>
              )}

              <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
                  <PtSpec label="Covers" value={ptTicketScopeText(ticket)} />
                  <PtSpec label="Ticket mode" value={ptTicketModeLabel(ticket.ticket_mode)} />
                  <PtSpec label="Technician" value={ticket.technician_name || 'Not assigned'} />
                  <PtSpec label="Location" value={[ticket.location_name, ticket.lsd].filter(Boolean).join(' · ') || '—'} />
                  <PtSpec label="PO" value={ticket.po_number || '—'} mono />
                  <PtSpec label="AFE" value={ticket.afe_number || '—'} mono />
                  <PtSpec label="Cost code" value={ticket.cost_code || '—'} mono />
                  <PtSpec label="Currency" value={ticket.currency || '—'} />
                  <PtSpec label="Created" value={formatInstant(ticket.created_at, tz) ?? '—'} />
                </dl>
              </section>

              {isDraft && attention.length > 0 && (
                <div role="status" data-testid="pt-ticket-attention" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="flex items-center gap-2 font-bold">
                    <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
                    {attention.length} line{attention.length === 1 ? ' needs' : 's need'} a price or quantity before the company man can sign.
                  </p>
                  <p className="mt-1 text-xs">Set a rate (or No charge), add the missing rate to the rate card and Reprice, or remove the line.</p>
                </div>
              )}

              <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card" data-testid="pt-ticket-lines">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-3">
                  <div>
                    <p className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Charges</p>
                    <h3 className="text-base font-extrabold text-white">Lines</h3>
                  </div>
                  {canAct && isDraft && (
                    <button type="button" className={PT_SECONDARY} onClick={() => setLineDialog({ kind: 'add' })} data-testid="pt-ticket-add-line">
                      <Plus className="size-4" aria-hidden="true" />
                      Add line
                    </button>
                  )}
                </div>
                {lines.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-fieldiq-text">This ticket has no charges.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                          <th scope="col" className="w-[88px] px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l) => {
                          const warn = ptLineNeedsAttention(l);
                          return (
                            <tr
                              key={l.id}
                              data-testid="pt-ticket-line"
                              data-money={l.money}
                              className={`border-t border-l-4 border-fieldiq-border ${warn ? 'border-l-fi-amber' : 'border-l-transparent'}`}
                            >
                              <td className="px-4 py-3">
                                <p className="text-sm font-semibold text-white">{l.description}</p>
                                <p className="mt-0.5 text-xs text-fieldiq-muted">
                                  {ptLineKindLabel(l.kind)}
                                  {l.source === 'auto' ? ' · automatic' : ''}
                                  {l.work_date ? ` · ${day(l.work_date)}` : ''}
                                </p>
                                {warn && l.unpriced_reason && (
                                  <p className="mt-1 flex items-start gap-1 text-xs text-fieldiq-text" data-testid="pt-ticket-line-reason">
                                    <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                                    {l.unpriced_reason}
                                  </p>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ptQtyText(l.quantity, l.unit)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">
                                <p className="font-mono">{ptRateText(l.rate, ticket.currency, l.unit)}</p>
                                <p className="mt-0.5 text-xs text-fieldiq-muted">{rateSourceText(l)}</p>
                              </td>
                              <td
                                className={`whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums ${warn ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`}
                                data-testid="pt-ticket-line-amount"
                              >
                                {ptLineAmountText(l, ticket.currency)}
                              </td>
                              <td className="px-4 py-3">
                                {canAct && isDraft && (
                                  <div className="flex justify-end gap-1">
                                    <button
                                      type="button"
                                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                      aria-label={`Edit ${l.description}`}
                                      onClick={() => setLineDialog({ kind: 'edit', line: l })}
                                      data-testid="pt-ticket-line-edit"
                                    >
                                      <Pencil className="size-4" aria-hidden="true" />
                                    </button>
                                    <button
                                      type="button"
                                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                      aria-label={`Remove ${l.description}`}
                                      onClick={() => setLineDialog({ kind: 'remove', line: l })}
                                      data-testid="pt-ticket-line-remove"
                                    >
                                      <Trash2 className="size-4" aria-hidden="true" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Subtotal (before tax)
                          </th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="pt-ticket-subtotal">
                            {ptSubtotalText(ticket)}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </section>

              {removed.length > 0 && (
                <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3" data-testid="pt-ticket-removed">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Removed lines ({removed.length})</p>
                  <ul className="mt-2 space-y-1 text-sm text-fieldiq-text">
                    {removed.map((l) => (
                      <li key={l.id}>
                        <span className="line-through">{l.description}</span>
                        <span className="text-fieldiq-muted"> — {l.delete_reason || 'no reason recorded'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </section>

      {ticket && voiding && <VoidDialog ticket={ticket} onClose={() => setVoiding(false)} />}
      {ticket && lineDialog && (
        <PtDialog
          open
          title={lineDialog.kind === 'add' ? `Add a line to ${ticket.ticket_number}` : lineDialog.kind === 'edit' ? 'Edit line' : 'Remove line'}
          onClose={() => setLineDialog(null)}
        >
          {lineDialog.kind === 'add' ? (
            <PtAddLineForm
              ticket={ticket}
              kinds={PT_OFFICE_LINE_KINDS}
              canPrice={canAct}
              day={ticket.work_date ?? today}
              onDone={() => setLineDialog(null)}
              onCancel={() => setLineDialog(null)}
            />
          ) : lineDialog.kind === 'edit' ? (
            <PtEditLineForm
              line={lineDialog.line}
              currency={ticket.currency}
              canPrice={canAct}
              onDone={() => setLineDialog(null)}
              onCancel={() => setLineDialog(null)}
            />
          ) : (
            <PtRemoveLineForm line={lineDialog.line} onDone={() => setLineDialog(null)} onCancel={() => setLineDialog(null)} />
          )}
        </PtDialog>
      )}
    </div>
  );
}
